import moment from 'moment';
import { docTypes } from 'utils/constants';
import {
    activities,
    activitySectors,
    corporateForms,
    matrimonialRegimes,
    positions,
} from 'utils/formData';
import {
    boolToStr,
    convertToBoolOrNull,
    createDocumentsIRI,
    initJsonField,
    jsonParseSafe,
} from 'utils/helpers';

const hasDifferentFiscalAddressFunc = fiscalAddress =>
    !!fiscalAddress &&
    (fiscalAddress.address !== '' || fiscalAddress.city !== '' || fiscalAddress.zipCode !== '');

const initConditions = user => ({
    termsAgreement:
        user?.investorProfile?.termsAgreement === true
            ? true
            : typeof user?.investorProfile === 'undefined',
    acceptedEmails:
        user?.investorProfile?.emailAgreement === true
            ? true
            : typeof user?.investorProfile === 'undefined',
});

const getAddressPP = user => {
    return {
        id: user?.investorProfile?.currentLocation?.id,
        address: user?.investorProfile?.currentLocation?.address || '',
        city: user?.investorProfile?.currentLocation?.city || '',
        zipCode: user?.investorProfile?.currentLocation?.zipCode || '',
        country: user?.investorProfile?.currentLocation?.country || 'FR',
        formattedAddress: null,
    };
};

const getAddressPM = user => {
    return {
        id: user?.investorCompany?.addressLocation?.id,
        address: user?.investorCompany?.addressLocation?.address || '',
        city: user?.investorCompany?.addressLocation?.city || '',
        zipCode: user?.investorCompany?.addressLocation?.zipCode || '',
        country: user?.investorCompany?.addressLocation?.country || 'FR',
        formattedAddress: null,
    };
};

export const createUserStep = ({ tunnelStepId, subscriptionId }) => ({
    subscription: `/api/subscriptions/${subscriptionId}`,
    tunnelStep: `/api/tunnel_steps/${tunnelStepId}`,
});

export const initContactInfoPP = (user, subscription) => {
    const foreignTaxObligation = jsonParseSafe(user?.investorProfile?.foreignTaxObligation, {});

    const politicallyExposedPersonReason = jsonParseSafe(
        user?.investorProfile?.politicallyExposedPersonReason,
        {}
    );

    const generalInfo = {
        ...getGeneralInfoPM(user, true),
        withCoSubscriber: 'false',
    };
    if (subscription?.withCoSubscriber) {
        generalInfo.withCoSubscriber = subscription?.withCoSubscriber ? 'true' : 'false';
    }

    const address = getAddressPP(user);
    const hasDifferentFiscalAddress = !!hasDifferentFiscalAddressFunc(
        user?.investorProfile?.taxLocation
    );

    const fiscalAddress = {
        id: user?.investorProfile?.taxLocation?.id,
        address: user?.investorProfile?.taxLocation?.address || '',
        city: user?.investorProfile?.taxLocation?.city || '',
        zipCode: user?.investorProfile?.taxLocation?.zipCode || '',
        country: user?.investorProfile?.taxLocation?.country || 'FR',
        formattedAddress: null,
    };

    const matrimonialRegime = {
        matrimonialRegime: user?.investorProfile?.matrimonialRegime || matrimonialRegimes[0].name,
    };

    const situation = {
        profession: user?.investorProfile?.jobTitle || '',
        situation: user?.investorProfile?.maritalStatus || 'Marié(e)',
        activitySector: user?.investorProfile?.activity || activitySectors[0].name,
        noOfPersons:
            typeof user?.investorProfile?.numberOfDependents !== 'undefined'
                ? user?.investorProfile?.numberOfDependents ?? ''
                : '',
        ...matrimonialRegime,
    };

    const fiscalSituationPart = {
        wasUSFiscalResident:
            user?.investorProfile?.usTaxResidency !== null ||
            user?.investorProfile?.usTaxResidency !== undefined
                ? String(user?.investorProfile?.usTaxResidency)
                : null,
        tin: user?.investorProfile?.tin || '',
        fiscalObligationOtherCountry:
            typeof foreignTaxObligation.choice === 'boolean'
                ? foreignTaxObligation.choice
                    ? 'true'
                    : 'false'
                : foreignTaxObligation.choice || null,
    };

    const fiscalSituation = {
        isUSPerson:
            typeof user?.investorProfile?.usPerson === 'boolean'
                ? user?.investorProfile?.usPerson
                    ? 'ineligible'
                    : 'eligible'
                : 'eligible',
        taxResident: user?.investorProfile?.taxCountry || 'FR',
        ...fiscalSituationPart,
    };

    const fiscalObligations = foreignTaxObligation.fields?.map(fo => ({
        country: fo.country,
        nif: fo.nif || '',
        nifReason: fo.nifReason || '',
    })) || [{ country: '', nif: '', nifReason: '' }];

    const politicallyExposed = {
        politicallyExposedPerson:
            user?.investorProfile?.politicallyExposedPerson !== null ||
            user?.investorProfile?.politicallyExposedPerson !== undefined
                ? String(user?.investorProfile?.politicallyExposedPerson)
                : null,
        politicallyExposedReason: {
            1: !!politicallyExposedPersonReason.choice?.includes('step5.question_15.option_1'),
            2: !!politicallyExposedPersonReason.choice?.includes('step5.question_15.option_2'),
            3: !!politicallyExposedPersonReason.choice?.includes('step5.question_15.option_3'),
        },
    };

    return {
        generalInfo,
        address,
        hasDifferentFiscalAddress,
        fiscalAddress,
        situation,
        fiscalSituation,
        fiscalObligations,
        politicallyExposed,
        conditions: initConditions(user),
    };
};

export const initCoSubscriber = (user, coSubscriber, subscription) => {
    const foreignTaxObligation = jsonParseSafe(
        coSubscriber?.investorProfile?.foreignTaxObligation,
        {}
    );

    const politicallyExposedPersonReason = jsonParseSafe(
        coSubscriber?.investorProfile?.politicallyExposedPersonReason,
        {}
    );

    const generalInfo = {
        id: coSubscriber?.investorProfile?.id,
        // a moment object used only by the date picker
        dateOfBirth: coSubscriber?.investorProfile?.birthDate
            ? moment(coSubscriber?.investorProfile?.birthDate)
            : null,
        birthDate: coSubscriber?.investorProfile?.birthDate || '',
        birthLocationId: coSubscriber?.investorProfile?.birthLocation?.id || 0,
        birthPlace: coSubscriber?.investorProfile?.birthLocation?.city || '',
        department: coSubscriber?.investorProfile?.birthLocation?.county || '',

        nativeCountry: coSubscriber?.investorProfile?.birthLocation?.country || 'FR',
        nationality: coSubscriber?.investorProfile?.nationality || 'FR',
        phoneNumber: coSubscriber?.phoneNumber || '',
        // the following are used on the client space only
        title: coSubscriber?.title || 'Monsieur',
        birthName: coSubscriber?.birthName || '',
        firstName: coSubscriber?.firstName || '',
        lastName: coSubscriber?.lastName || '',
        email: coSubscriber?.email || '',

        withCoSubscriber: 'false',
    };
    if (subscription?.withCoSubscriber) {
        generalInfo.withCoSubscriber = subscription?.withCoSubscriber ? 'true' : 'false';
    }

    const address = getAddressPP(
        coSubscriber?.investorProfile?.currentLocation?.address ? coSubscriber : user
    );
    const hasDifferentFiscalAddress = !!hasDifferentFiscalAddressFunc(
        coSubscriber?.investorProfile?.taxLocation
    );

    const matrimonialRegime = {
        matrimonialRegime:
            coSubscriber?.investorProfile?.matrimonialRegime || matrimonialRegimes[0].name,
    };

    const situation = {
        profession: coSubscriber?.investorProfile?.jobTitle || '',
        situation: coSubscriber?.investorProfile?.maritalStatus || 'Marié(e)',
        activitySector: coSubscriber?.investorProfile?.activity || activitySectors[0].name,
        noOfPersons:
            typeof coSubscriber?.investorProfile?.numberOfDependents !== 'undefined'
                ? coSubscriber?.investorProfile?.numberOfDependents ?? ''
                : '',
        ...matrimonialRegime,
    };

    const fiscalSituationPart = {
        wasUSFiscalResident:
            coSubscriber?.investorProfile?.usTaxResidency !== null ||
            coSubscriber?.investorProfile?.usTaxResidency !== undefined
                ? String(coSubscriber?.investorProfile?.usTaxResidency)
                : null,
        tin: coSubscriber?.investorProfile?.tin || '',
        fiscalObligationOtherCountry:
            typeof foreignTaxObligation.choice === 'boolean'
                ? foreignTaxObligation.choice
                    ? 'true'
                    : 'false'
                : foreignTaxObligation.choice || null,
    };

    const fiscalSituation = {
        isUSPerson:
            typeof coSubscriber?.investorProfile?.usPerson === 'boolean'
                ? coSubscriber?.investorProfile?.usPerson
                    ? 'ineligible'
                    : 'eligible'
                : 'eligible',
        taxResident: coSubscriber?.investorProfile?.taxCountry || 'FR',
        ...fiscalSituationPart,
    };

    const fiscalObligations = foreignTaxObligation.fields?.map(fo => ({
        country: fo.country,
        nif: fo.nif || '',
        nifReason: fo.nifReason || '',
    })) || [{ country: '', nif: '', nifReason: '' }];

    const politicallyExposed = {
        politicallyExposedPerson:
            coSubscriber?.investorProfile?.politicallyExposedPerson !== null ||
            coSubscriber?.investorProfile?.politicallyExposedPerson !== undefined
                ? String(coSubscriber?.investorProfile?.politicallyExposedPerson)
                : null,
        politicallyExposedReason: {
            1: politicallyExposedPersonReason.choice?.includes('step5.question_15.option_1')
                ? true
                : false,
            2: politicallyExposedPersonReason.choice?.includes('step5.question_15.option_2')
                ? true
                : false,
            3: politicallyExposedPersonReason.choice?.includes('step5.question_15.option_3')
                ? true
                : false,
        },
    };

    return {
        generalInfo,
        address,
        hasDifferentFiscalAddress,
        situation,
        fiscalSituation,
        fiscalObligations,
        politicallyExposed,
        conditions: initConditions(coSubscriber),
    };
};

const getGeneralInfoPM = (user, withBasicInfo = false) => {
    const basicInfo = withBasicInfo
        ? {
              title: user?.title || 'Monsieur',
              birthName: user?.birthName || '',
              firstName: user?.firstName || '',
              lastName: user?.lastName || '',
              email: user?.email || '',
          }
        : {};

    return {
        id: user?.investorProfile?.id,
        ...basicInfo,
        // a moment object used only by the date picker
        dateOfBirth: user?.investorProfile?.birthDate
            ? moment(user?.investorProfile?.birthDate)
            : null,
        birthDate: user?.investorProfile?.birthDate || '',
        birthLocationId: user?.investorProfile?.birthLocation?.id,
        birthPlace: user?.investorProfile?.birthLocation?.city || '',
        department: user?.investorProfile?.birthLocation?.county || '',
        nativeCountry: user?.investorProfile?.birthLocation?.country || 'FR',
        nationality: user?.investorProfile?.nationality || 'FR',
        phoneNumber: user?.phoneNumber || '',
        fax: user?.investorProfile?.fax || '',
    };
};
export const initContactInfoPM = user => {
    const politicallyExposedPersonReason =
        jsonParseSafe(user?.investorProfile?.politicallyExposedPersonReason) || {};

    return {
        generalInfo: getGeneralInfoPM(user),
        address: getAddressPP(user),
        hasDifferentFiscalAddress: !!hasDifferentFiscalAddressFunc(
            user?.investorProfile?.taxLocation
        ),
        fiscalAddress: {
            id: user?.investorProfile?.taxLocation?.id,
            address: user?.investorProfile?.taxLocation?.address || '',
            city: user?.investorProfile?.taxLocation?.city || '',
            zipCode: user?.investorProfile?.taxLocation?.zipCode || '',
            country: user?.investorProfile?.taxLocation?.country || 'FR',
            formattedAddress: null,
        },

        situation: {
            profession: user?.investorProfile?.jobTitle || '',
            activitySector: user?.investorProfile?.activity || activitySectors[0].name,
        },

        fiscalSituation: {
            isUSPerson:
                typeof user?.investorProfile?.usPerson === 'boolean'
                    ? user?.investorProfile?.usPerson
                        ? 'ineligible'
                        : 'eligible'
                    : '',
            taxResident: user?.investorProfile?.taxCountry || 'FR',
        },

        politicallyExposed: {
            politicallyExposedPerson:
                user?.investorProfile?.politicallyExposedPerson !== null ||
                user?.investorProfile?.politicallyExposedPerson !== undefined
                    ? String(user?.investorProfile?.politicallyExposedPerson)
                    : null,
            politicallyExposedReason: {
                1: !!politicallyExposedPersonReason.choice?.includes('step5.question_15.option_1'),
                2: !!politicallyExposedPersonReason.choice?.includes('step5.question_15.option_2'),
                3: !!politicallyExposedPersonReason.choice?.includes('step5.question_15.option_3'),
            },
        },
    };
};

export const initFinancialInstruments = (kycSurvey, isResubmit) => ({
    opcvmActionInvestment: isResubmit ? kycSurvey?.opcvmActionInvestment : '',
    opcvmObligationInvestment: isResubmit ? kycSurvey?.opcvmObligationInvestment : '',
    opcvmMonetaryInvestment: isResubmit ? kycSurvey?.opcvmMonetaryInvestment : '',
    listedShareInvestment: isResubmit ? kycSurvey?.listedShareInvestment : '',
    unlistedShareInvestment: isResubmit ? kycSurvey?.unlistedShareInvestment : '',
    otherFundInvestment: isResubmit ? kycSurvey?.otherFundInvestment : '',
    otherInvestment: isResubmit ? kycSurvey?.otherInvestment : '',
});

export const initFinancialStatus = (userAdequacy, isResubmit) => ({
    loseAllFundsEffect: isResubmit ? userAdequacy?.loseAllFundsEffect : null,
    noIncomeRisk: isResubmit ? userAdequacy?.noIncomeRisk : null,
    percentageInvestment: isResubmit ? userAdequacy?.percentageInvestment : null,
});

export const sendInvestorProfilePP = payload => {
    const maritalStatus = payload.situation.situation;
    const numberOfDependents = parseInt(payload.situation.noOfPersons);

    const matrimonialRegime = payload.situation.matrimonialRegime;
    const usTaxResidency = convertToBoolOrNull(payload.fiscalSituation.wasUSFiscalResident);
    const tin = payload.fiscalSituation.tin;

    const foreignTaxObligation = JSON.stringify({
        choice: payload.fiscalSituation.fiscalObligationOtherCountry,
        fields:
            payload.fiscalSituation.fiscalObligationOtherCountry === 'true'
                ? payload.fiscalObligations.map(fo => ({
                      country: fo.country,
                      nif: fo.nif,
                      nifReason: fo.nifReason,
                  }))
                : [],
    });

    const taxLocation = payload.hasDifferentFiscalAddress
        ? {
              country: payload.fiscalAddress.country,
              address: payload.fiscalAddress.address,
              city: payload.fiscalAddress.city,
              zipCode: payload.fiscalAddress.zipCode,
              formattedAddress: payload.fiscalAddress.formattedAddress,
          }
        : { country: null, address: '', city: '', zipCode: '', formattedAddress: null };

    return {
        investorProfile: {
            birthDate: payload.generalInfo.birthDate || null, // empty string is not valid format
            nationality: payload.generalInfo.nationality,
            jobTitle: payload.situation.profession,
            usPerson: payload.fiscalSituation.isUSPerson === 'ineligible',
            taxCountry: payload.fiscalSituation.taxResident,
            termsAgreement: payload.conditions.termsAgreement,
            emailAgreement: payload.conditions.acceptedEmails,
            activity: payload.situation.activitySector,
            maritalStatus,
            numberOfDependents,
            matrimonialRegime,
            usTaxResidency,
            tin,
            foreignTaxObligation,
            birthLocation: {
                county: payload.generalInfo.department,
                city: payload.generalInfo.birthPlace,
                country: payload.generalInfo.nativeCountry,
            },
            currentLocation: {
                country: payload.address.country,
                address: payload.address.address,
                zipCode: payload.address.zipCode,
                city: payload.address.city,
                formattedAddress: payload.address.formattedAddress,
            },
            taxLocation,
            politicallyExposedPerson: convertToBoolOrNull(
                payload.politicallyExposed.politicallyExposedPerson
            ),
            politicallyExposedPersonReason: JSON.stringify({
                choice: Object.keys(payload.politicallyExposed.politicallyExposedReason)
                    .filter(
                        key => payload.politicallyExposed.politicallyExposedReason[key] === true
                    )
                    .map(key => `step5.question_15.option_${key}`),
            }),
        },
        phoneNumber: payload.generalInfo.phoneNumber.replace(/\s/g, ''),
        subscriptions: [
            {
                withCoSubscriber: payload.generalInfo.withCoSubscriber === 'true',
            },
        ],
    };
};

export const sendInvestorProfilePM = (payload, legalRepresentativeDocId) => {
    let docId = payload.corporateInfo?.legalRepresentativeDocument?.id || null;
    if (legalRepresentativeDocId?.[0] && legalRepresentativeDocId[0]?.id) {
        docId = legalRepresentativeDocId[0].id;
    }
    return {
        phoneNumber: payload.generalInfo.phoneNumber.replace(/\s/g, ''),
        investorProfile: {
            activity: payload.situation.activitySector,
            birthDate: payload.generalInfo.birthDate || null,
            nationality: payload.generalInfo.nationality,
            fax: payload.generalInfo.fax || '',
            jobTitle: payload.situation.profession,
            usPerson: payload.fiscalSituation.isUSPerson === 'ineligible',
            taxCountry: payload.fiscalSituation.taxResident,
            birthLocation: {
                county: payload.generalInfo.department,
                city: payload.generalInfo.birthPlace,
                country: payload.generalInfo.nativeCountry,
            },
            currentLocation: {
                country: payload.address.country,
                address: payload.address.address,
                zipCode: payload.address.zipCode,
                city: payload.address.city,
                formattedAddress: payload.address.formattedAddress,
            },
            politicallyExposedPerson: convertToBoolOrNull(
                payload.politicallyExposed.politicallyExposedPerson
            ),
            politicallyExposedPersonReason: JSON.stringify({
                choice: Object.keys(payload.politicallyExposed.politicallyExposedReason)
                    .filter(
                        key => payload.politicallyExposed.politicallyExposedReason[key] === true
                    )
                    .map(key => `step5.question_15.option_${key}`),
            }),
        },
        investorCompany: {
            activity: JSON.stringify({
                choice: payload.corporateInfo.activity,
                ...(payload.corporateInfo.activity === activities.other && {
                    other: payload.corporateInfo.otherActivity,
                }),
            }),
            shareCapital: +payload.corporateInfo.shareCapital,
            currency: payload.corporateInfo.currency,
            rcsRegistrationAddress: payload.corporateInfo.rcsRegistrationAddress,
            rcsNumber: payload.corporateInfo.rcsNumber,
            taxSystem: payload.corporateInfo.taxSystem,
            signatoryOnKbis: convertToBoolOrNull(
                payload.corporateInfo.signatoryOnKbis,
                'yes',
                'no'
            ),
            representativeRole: payload.corporateInfo.representativeRole,
            representativeName: payload.corporateInfo.representativeName,
            ownAccountAgreement: convertToBoolOrNull(
                payload.corporateInfo.ownAccountAgreement,
                'yes',
                'no'
            ),
            ...(docId && { legalRepresentativeDocument: `/api/documents/${docId}` }),
            legalEntityIdentificationNumber: payload.corporateInfo.legalEntityIdentificationNumber,
            addressLocation: payload.corporateInfo.currentAddress,
            postalLocation: payload.corporateInfo.hasDifferentPostalAddress
                ? payload.corporateInfo.postalAddress
                : {
                      address: '',
                      zipCode: '',
                      city: '',
                      country: null,
                      formattedAddress: null,
                  },
        },
    };
};

export const sendCoSubscriber = payload => {
    const foreignTaxObligation = JSON.stringify({
        choice: payload.fiscalSituation.fiscalObligationOtherCountry,
        fields:
            payload.fiscalSituation.fiscalObligationOtherCountry === 'true'
                ? payload.fiscalObligations.map(fo => ({
                      country: fo.country,
                      nif: fo.nif,
                      nifReason: fo.nifReason,
                  }))
                : [],
    });

    return {
        phoneNumber: payload.generalInfo.phoneNumber.replace(/\s/g, ''),
        email: payload.generalInfo.email,
        firstName: payload.generalInfo.firstName,
        lastName: payload.generalInfo.lastName,
        title: payload.generalInfo.title,
        investorProfile: {
            birthDate: payload.generalInfo.birthDate || null, // empty string is not valid format
            nationality: payload.generalInfo.nationality,
            jobTitle: payload.situation.profession,
            usPerson: payload.fiscalSituation.isUSPerson === 'ineligible',
            taxCountry: payload.fiscalSituation.taxResident,
            termsAgreement: payload.conditions.termsAgreement,
            emailAgreement: payload.conditions.acceptedEmails,
            activity: payload.situation.activitySector,
            maritalStatus: payload.situation.situation,
            numberOfDependents: parseInt(payload.situation.noOfPersons),
            matrimonialRegime: payload.situation.matrimonialRegime,
            usTaxResidency: convertToBoolOrNull(payload.fiscalSituation.wasUSFiscalResident),
            tin: payload.fiscalSituation.tin,
            foreignTaxObligation,
            birthLocation: {
                county: payload.generalInfo.department,
                city: payload.generalInfo.birthPlace,
                country: payload.generalInfo.nativeCountry,
            },
            currentLocation: {
                country: payload.address.country,
                address: payload.address.address,
                zipCode: payload.address.zipCode,
                city: payload.address.city,
                formattedAddress: payload.address.formattedAddress,
            },
            politicallyExposedPerson: convertToBoolOrNull(
                payload.politicallyExposed.politicallyExposedPerson
            ),
            politicallyExposedPersonReason: JSON.stringify({
                choice: Object.keys(payload.politicallyExposed.politicallyExposedReason)
                    .filter(
                        key => payload.politicallyExposed.politicallyExposedReason[key] === true
                    )
                    .map(key => `step5.question_15.option_${key}`),
            }),
        },
    };
};

export const initKnowClientFPCI = (kycSurvey, isResubmit) => {
    const incomeSource = jsonParseSafe(kycSurvey?.incomeSource) || false;

    return {
        annualNetIncome: isResubmit ? boolToStr(kycSurvey?.annualNetIncome, 'Oui', 'Non') : '',
        incomeSource: {
            choice: {
                'step5.question_2.option_1':
                    incomeSource?.choice?.indexOf('step5.question_2.option_1') > -1,
                'step5.question_2.option_2':
                    incomeSource?.choice?.indexOf('step5.question_2.option_2') > -1,
                'step5.question_2.option_3':
                    incomeSource?.choice?.indexOf('step5.question_2.option_3') > -1,
                'step5.question_2.option_4':
                    incomeSource?.choice?.indexOf('step5.question_2.option_4') > -1,
                'step5.question_2.option_5':
                    incomeSource?.choice?.indexOf('step5.question_2.option_5') > -1,
                'step5.question_2.option_6':
                    incomeSource?.choice?.indexOf('step5.question_2.option_6') > -1,
                'general_options.other':
                    incomeSource?.choice?.indexOf('general_options.other') > -1,
            },
            other: incomeSource?.other || '',
        },
        heritage: {
            heritageAmount: isResubmit ? kycSurvey?.heritageAmount : '',
            realEstatePercentage: isResubmit ? kycSurvey?.realEstatePercentage : '',
            listedSharePercentage: isResubmit ? kycSurvey?.listedSharePercentage : '',
            unlistedSharePercentage: isResubmit ? kycSurvey?.unlistedSharePercentage : '',
            opcvmPercentage: isResubmit ? kycSurvey?.opcvmPercentage : '',
            availableCashPercentage: isResubmit ? kycSurvey?.availableCashPercentage : '',
        },
        debt: {
            indebtednessAmount: isResubmit ? kycSurvey?.indebtednessAmount : '',
            mainResidencePercentage: isResubmit ? kycSurvey?.mainResidencePercentage : '',
            rentalInvestmentPercentage: isResubmit ? kycSurvey?.rentalInvestmentPercentage : '',
            otherCommitmentsPercentage: isResubmit ? kycSurvey?.otherCommitmentsPercentage : '',
            loanRepaymentPercentage: isResubmit ? kycSurvey?.loanRepaymentPercentage : '',
            otherDebtsPercentage: isResubmit ? kycSurvey?.otherDebtsPercentage : '',
        },
    };
};

export const sendKnowClientPP = ({ annualNetIncome, incomeSource, heritage, debt }) => ({
    subscriptions: [
        {
            kycSurvey: {
                annualNetIncome: convertToBoolOrNull(annualNetIncome, 'Oui', 'Non'),
                incomeSource: !!incomeSource
                    ? JSON.stringify({
                          choice: Object.keys(incomeSource?.choice).filter(
                              option => incomeSource?.choice[option] && option
                          ),
                          other: incomeSource?.other,
                      })
                    : JSON.stringify({}),
                heritageAmount: heritage?.heritageAmount || 0,
                realEstatePercentage: heritage?.heritageAmount ? +heritage.realEstatePercentage : 0,
                listedSharePercentage: heritage?.heritageAmount
                    ? +heritage.listedSharePercentage
                    : 0,
                unlistedSharePercentage: heritage?.heritageAmount
                    ? +heritage.unlistedSharePercentage
                    : 0,
                opcvmPercentage: heritage?.heritageAmount ? +heritage.opcvmPercentage : 0,
                availableCashPercentage: heritage?.heritageAmount
                    ? +heritage.availableCashPercentage
                    : 0,
                indebtednessAmount: debt?.indebtednessAmount || 0,
                mainResidencePercentage: debt?.indebtednessAmount
                    ? +debt.mainResidencePercentage
                    : 0,
                rentalInvestmentPercentage: debt?.indebtednessAmount
                    ? +debt.rentalInvestmentPercentage
                    : 0,
                otherCommitmentsPercentage: debt?.indebtednessAmount
                    ? +debt.otherCommitmentsPercentage
                    : 0,
                loanRepaymentPercentage: debt?.indebtednessAmount
                    ? +debt.loanRepaymentPercentage
                    : 0,
                otherDebtsPercentage: debt?.indebtednessAmount ? +debt.otherDebtsPercentage : 0,
            },
        },
    ],
});

export const sendKnowClientPM = ({
    totalBalanceLevel,
    totalTurnoverLevel,
    totalEquityLevel,
    ...form
}) => ({
    subscriptions: [
        {
            kycSurvey: {
                totalBalanceLevel,
                totalTurnoverLevel,
                totalEquityLevel,
                heritageAmount: +form?.heritageAmount,
                realEstatePercentage: +form?.heritageAmount ? +form.realEstatePercentage : 0,
                listedSharePercentage: +form?.heritageAmount ? +form.listedSharePercentage : 0,
                unlistedSharePercentage: +form?.heritageAmount ? +form.unlistedSharePercentage : 0,
                opcvmPercentage: +form?.heritageAmount ? +form.opcvmPercentage : 0,
                availableCashPercentage: +form?.heritageAmount ? +form.availableCashPercentage : 0,
                assetInvestor: convertToBoolOrNull(form.assetInvestor, 'Oui', 'Non'),
                assetPercentage: form.assetPercentage,
                indebtednessAmount: +form?.indebtednessAmount,
                mainResidencePercentage: +form?.indebtednessAmount
                    ? +form.mainResidencePercentage
                    : 0,
                rentalInvestmentPercentage: +form?.indebtednessAmount
                    ? +form.rentalInvestmentPercentage
                    : 0,
                otherCommitmentsPercentage: +form?.indebtednessAmount
                    ? +form.otherCommitmentsPercentage
                    : 0,
                loanRepaymentPercentage: +form?.indebtednessAmount
                    ? +form.loanRepaymentPercentage
                    : 0,
                otherDebtsPercentage: +form?.indebtednessAmount ? +form.otherDebtsPercentage : 0,
            },
        },
    ],
});

export const sendExperience = ({ form_0, form_1, form_2, form_3, form_4 }) => ({
    subscriptions: [
        {
            kycSurvey: {
                financialInstrument: JSON.stringify({
                    choice: Object.keys(form_0.choice).filter(
                        option => form_0.choice[option] && option
                    ),
                    other: form_0.other,
                }),
                investmentsManagement: JSON.stringify({
                    choice: Object.keys(form_1.choice)
                        .filter(option => form_1.choice[option] && option)
                        .concat(form_1.other ? [form_1.other] : []),
                }),
                opcvmActionInvestment: form_2.opcvmActionInvestment,
                opcvmObligationInvestment: form_2.opcvmObligationInvestment,
                opcvmMonetaryInvestment: form_2.opcvmMonetaryInvestment,
                listedShareInvestment: form_2.listedShareInvestment,
                unlistedShareInvestment: form_2.unlistedShareInvestment,
                otherFundInvestment: form_2.otherFundInvestment,
                otherInvestment: form_2.otherInvestment,
                transactionsCarriedOut: typeof form_3 !== 'undefined' ? parseInt(form_3) : form_3,
            },
            ...(form_4 && {
                userAdequacy: {
                    question6: form_4.question6,
                    question7: form_4.question7,
                    question8: convertToBoolOrNull(form_4.question8, 'Oui', 'Non'),
                    question9: form_4.question9,
                    question10: form_4.question10,
                },
            }),
        },
    ],
});

export const sendInvestmentCharacteristics = ({ investingSum }) => ({
    subscriptions: [{ investmentCharacteristics: { investingSum } }],
});

export const sendBankAccount = RIBData => {
    const {
        bankAccount,
        bankAccountTitle,
        bankIdentifierCode,
        frenchBased,
        paymentMethod,
        selectedRIB,
        nif,
        nifReason,
        taxExemption,
        coSubscriberNif,
        coSubscriberNifReason,
    } = RIBData;

    const bankDetails = new FormData();
    bankDetails.append('file', selectedRIB);
    bankDetails.append(
        'body',
        JSON.stringify({
            subscriptions: [
                {
                    ...(coSubscriberNif || coSubscriberNifReason
                        ? {
                              coSubscriber: {
                                  investorProfile: {
                                      nif: coSubscriberNif || '',
                                      nifReason: coSubscriberNifReason || '',
                                  },
                              },
                          }
                        : {}),
                    bankingStatement: {
                        bankAccountNumber: bankAccount.replace(/\s/g, ''),
                        bankIdentifierCode: bankIdentifierCode.replace(/\s/g, ''),
                        bankAccountNumberTitle: bankAccountTitle.replace(/\s/g, '') || undefined,
                        frenchBased: frenchBased !== '' ? frenchBased : undefined,
                        paymentMethod,
                    },
                },
            ],
            investorProfile: {
                nif,
                nifReason,
                taxExemption: typeof taxExemption !== 'boolean' ? null : taxExemption,
            },
        })
    );

    return bankDetails;
};

export const sendRegulationAgreement = fundRegulationAgreement => ({
    subscriptions: [{ fundRegulationAgreement }],
});

export const sendDiciAgreement = payload => ({
    subscriptions: [
        {
            diciAgreement: payload.diciAgreement,
            ...(payload.transactionsAgreement && {
                transactionsAgreement: payload.transactionsAgreement,
            }),
        },
    ],
});

export const initUserPayload = (
    user,
    tunnelSubscription,
    queryEmail,
    queryFirstName,
    queryLastName,
    queryCivility
) => ({
    role: tunnelSubscription?.role.id || 0,
    title: user?.title || queryCivility || 'Monsieur',
    birthName: user?.birthName || '',
    firstName: user?.firstName || queryFirstName || '',
    lastName: user?.lastName || queryLastName || '',
    email: user?.email || queryEmail || '',
    password: '',
    confirmPassword: '',
    conditions: initConditions(user),
});

export const initCompanyPayload = (
    user,
    tunnelSubscription,
    queryEmail,
    queryFirstName,
    queryLastName,
    queryCivility
) => {
    // init activity
    const [activity, otherActivity] = initJsonField(
        user?.investorCompany?.activity,
        activities.agriculture,
        activities.other
    );

    // init corporate form
    const [corporateForm, otherCorporateForm] = initJsonField(
        user?.investorCompany?.corporateForm,
        corporateForms.SAS,
        corporateForms.other
    );

    return {
        id: user?.investorCompany?.id,
        role: tunnelSubscription?.role.id || 0,
        title: user?.title || queryCivility || 'Monsieur',
        birthName: user?.birthName || '',
        firstName: user?.firstName || queryFirstName || '',
        lastName: user?.lastName || queryLastName || '',
        email: user?.email || queryEmail || '',
        currentAddress: getAddressPM(user),
        shareCapital: user?.investorCompany?.shareCapital || '',
        currency: user?.investorCompany?.currency || 'EUR',
        siret: user?.investorCompany?.siret || '',
        activity,
        otherActivity,
        corporateName: user?.investorCompany?.corporateName || '',
        corporateForm,
        otherCorporateForm,
        rcsNumber: user?.investorCompany?.rcsNumber || '',
        taxSystem: user?.investorCompany?.taxSystem || 'IS',
        signatoryOnKbis: boolToStr(user?.investorCompany?.signatoryOnKbis, 'yes', 'no'),
        representativeName: user?.investorCompany?.representativeName || '',
        representativeRole: user?.investorCompany?.representativeRole || '',
        ownAccountAgreement: boolToStr(user?.investorCompany?.ownAccountAgreement, 'yes', 'no'),
        password: '',
        confirmPassword: '',
        conditions: initConditions(user),
    };
};

export const initInvestorDetailsPMPayload = user => {
    const politicallyExposedPersonReason =
        jsonParseSafe(user?.investorProfile?.politicallyExposedPersonReason) || {};

    return {
        generalInfo: getGeneralInfoPM(user),
        address: getAddressPP(user),
        corporateInfo: initCompanyContactInfoPayload(user, null),
        situation: {
            profession: user?.investorProfile?.jobTitle || '',
            activitySector: user?.investorProfile?.activity || activitySectors[0].name,
        },
        fiscalSituation: {
            isUSPerson:
                typeof user?.investorProfile?.usPerson === 'boolean'
                    ? user?.investorProfile?.usPerson
                        ? 'ineligible'
                        : 'eligible'
                    : '',
            taxResident: user?.investorProfile?.taxCountry || 'FR',
        },

        politicallyExposed: {
            politicallyExposedPerson:
                user?.investorProfile?.politicallyExposedPerson !== null ||
                user?.investorProfile?.politicallyExposedPerson !== undefined
                    ? String(user?.investorProfile?.politicallyExposedPerson)
                    : null,
            politicallyExposedReason: {
                1: !!politicallyExposedPersonReason.choice?.includes('step5.question_15.option_1'),
                2: !!politicallyExposedPersonReason.choice?.includes('step5.question_15.option_2'),
                3: !!politicallyExposedPersonReason.choice?.includes('step5.question_15.option_3'),
            },
        },
    };
};
export const initCompanyContactInfoPayload = (user, tunnelSubscription) => {
    const base = initCompanyPayload(user, tunnelSubscription);
    base.postalAddress = {
        id: user?.investorCompany?.postalLocation?.id,
        address: user?.investorCompany?.postalLocation?.address || '',
        city: user?.investorCompany?.postalLocation?.city || '',
        zipCode: user?.investorCompany?.postalLocation?.zipCode || '',
        country: user?.investorCompany?.postalLocation?.country || 'FR',
        formattedAddress: null,
    };
    base.hasDifferentPostalAddress = !!(
        user?.investorCompany?.postalLocation?.address ||
        user?.investorCompany?.postalLocation?.city ||
        user?.investorCompany?.postalLocation?.zipCode
    );
    base.legalEntityIdentificationNumber =
        user?.investorCompany?.legalEntityIdentificationNumber || '';
    base.rcsRegistrationAddress = user?.investorCompany?.rcsRegistrationAddress || '';
    base.legalRepresentativeDocument = user?.investorCompany?.legalRepresentativeDocument || null;
    return base;
};

export const initDistributorPayload = (user, tunnelSubscription) => {
    // init corporate form
    const [corporateForm, otherCorporateForm] = initJsonField(
        user?.distributorCompany?.corporateForm,
        corporateForms.SAS,
        corporateForms.other
    );

    return {
        title: user?.title || 'Monsieur',
        email: user?.email || '',
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        role: tunnelSubscription?.role.id || 0,
        currentAddress: {
            country: user?.distributorCompany?.currentLocation?.country || 'FR',
            city: user?.distributorCompany?.currentLocation?.city || '',
            zipCode: user?.distributorCompany?.currentLocation?.zipCode || '',
            formattedAddress: user?.distributorCompany?.currentLocation?.formattedAddress || '',
            address: user?.distributorCompany?.currentLocation?.address || '',
        },
        shareCapital: user?.distributorCompany?.shareCapital || '',
        currency: user?.distributorCompany?.currency || 'EUR',
        phoneNumber: user?.phoneNumber || '',
        corporateName: user?.distributorCompany?.corporateName || '',
        organization: boolToStr(user?.distributorCompany?.organization),
        corporateForm,
        otherCorporateForm,
        legalRepresentant:
            user?.distributorCompany?.legalRepresentant === true
                ? 'yes'
                : typeof user?.distributorCompany?.legalRepresentant === 'undefined'
                ? false
                : 'no',
        function: user?.distributorCompany?.function || positions[0],
        representativeRole: user?.distributorCompany?.representativeRole || positions[0],
        representativeName: user?.distributorCompany?.representativeName || '',
        password: '',
        confirmPassword: '',
        conditions: initConditions(user),
    };
};

export const startAddressVerification = documentType => ({
    investorProfile: {
        addressDocuments: [{ documentType }],
    },
});

export const sendAutoCertification = ({
    autoCertification: { dacStatut, fatcaStatut, fatcaStatutOther },
    selectedFiles,
}) => {
    const payload = {
        investorCompany: {
            certification: {
                DacCrsStatus: JSON.stringify({ choice: !!dacStatut ? [dacStatut] : [] }),
                FatcaStatus: JSON.stringify({ choice: [fatcaStatut], other: fatcaStatutOther }),
            },
        },
    };

    let formData = new FormData();
    selectedFiles.forEach(file => formData.append('files[]', file));
    formData.append('body', JSON.stringify(payload));

    return formData;
};

export const sendCompanyBeneficiaries = (
    { selectedFiles, selectedListFiles, reducedVigilance, beneficiariesPP },
    documentIDs
) => {
    let formData = new FormData();
    selectedFiles.forEach(file => formData.append('files[vigilance][]', file));
    selectedListFiles
        .filter(file => !file?.id)
        .forEach(file => formData.append('files[identify][]', file));
    let payload = { ...reducedVigilance };

    if (beneficiariesPP) {
        const documentsIRI = documentIDs ? documentIDs.map(doc => `/api/documents/${doc.id}`) : [];

        payload.beneficiariesPP = beneficiariesPP
            .filter(beneficiary => !!beneficiary.firstname && !!beneficiary.lastname) // filter out invalid rows
            .map(beneficiary => {
                const documents = beneficiary.documents;
                const documentsNoId = documents.filter(doc => !doc.id);
                const documentsWithId = documents
                    .filter(doc => doc?.id)
                    .map(doc => `/api/documents/${doc.id}`);
                const documentsIndex = documentsNoId.length;
                const { politicallyExposedReason, ...restBeneficiary } = beneficiary;

                const commonPayload = {
                    ...restBeneficiary,
                    politicallyExposedPerson: restBeneficiary.politicallyExposedPerson === 'true',
                    politicallyExposedPersonReason: JSON.stringify({
                        choice: Object.keys(politicallyExposedReason)
                            .filter(key => politicallyExposedReason[key] === true)
                            .map(key => `step5.question_15.option_${key}`),
                    }),
                };

                if (documentsIndex > 0) {
                    return {
                        ...commonPayload,
                        documents: [...documentsIRI.splice(0, documentsIndex), ...documentsWithId],
                    };
                } else {
                    return {
                        ...commonPayload,
                        documents: documentsWithId,
                    };
                }
            });
    }

    formData.append('body', JSON.stringify({ investorCompany: payload }));

    return formData;
};

export const initDistributorDocuments = (distributorCompany, bankingStatement) => ({
    legalRepresentantAuthority: distributorCompany?.legalRepresentativeDocument || null,
    oriasAttestation: distributorCompany?.certificateORIAS || null,
    beneficiariesRegister: distributorCompany?.beneficiariesRegister || null,
    beneficiariesIdentityDocuments: distributorCompany?.beneficiariesIdentityDocuments || [],
    legalRepresentativesIdentityDocuments:
        distributorCompany?.legalRepresentativesIdentityDocuments || [],
    kbis: distributorCompany?.kbis || null,
    insuranceCertificate: distributorCompany?.insuranceCertificate || null,
    legalRepresentantId: distributorCompany?.legalRepresentativeIdentityDocument || null,
    signatoryId: distributorCompany?.signatoryIdentityDocument || null,
    rib: bankingStatement?.ribDocument || null,
    logo: distributorCompany?.companyLogo || null,
});

export const sendDistributorDocuments = documents => {
    const findDocument = fileType => documents.find(doc => doc.subtype === fileType) || null;
    const getDocIRI = fileType => createDocumentsIRI(findDocument(fileType)?.id);

    return {
        distributorCompany: {
            legalRepresentativeDocument: getDocIRI('legalRepresentantAuthority'),
            certificateORIAS: getDocIRI('oriasAttestation'),
            kbis: getDocIRI('kbis'),
            insuranceCertificate: getDocIRI('insuranceCertificate'),
            beneficiariesRegister: getDocIRI('beneficiariesRegister'),
            beneficiariesIdentityDocuments: documents
                .filter(doc => doc.subtype === 'beneficiariesIdentityDocuments')
                .map(doc => createDocumentsIRI(doc.id)),
            legalRepresentativesIdentityDocuments: documents
                .filter(doc => doc.subtype === 'legalRepresentativesIdentityDocuments')
                .map(doc => createDocumentsIRI(doc.id)),
            legalRepresentativeIdentityDocument: getDocIRI('legalRepresentantId'),
            signatoryIdentityDocument: getDocIRI('signatoryId'),
            companyLogo: getDocIRI('logo'),
        },
        subscriptions: [
            {
                bankingStatement: {
                    ribDocument: getDocIRI('rib'),
                },
            },
        ],
    };
};

export const sendInvestorEligibility = (
    {
        transactionsCompletedDocuments,
        portfolioValueDocuments,
        professionalPositionHeldDocuments,
        userAdequacy,
        subcategory,
        ...payload
    },
    savedBalanceSheetTotalDocs,
    savedNetSalesOrRevenuesDocs,
    savedHigherEquityDocs,
    savedExemptionConditionsProofDocs
) => {
    let balanceSheetTotalDocuments = [];
    if (payload.fixedCriteria?.includes('step34_fpci_pm_real.question_16.option_1')) {
        balanceSheetTotalDocuments = payload.balanceSheetTotalDocuments
            .reduce((acc, curr) => {
                if (curr?.id) {
                    acc = acc.concat(`api/documents/${curr.id}`);
                    return acc;
                }
                return acc;
            }, [])
            .concat(savedBalanceSheetTotalDocs.map(doc => `api/documents/${doc.id}`));
    }

    let netSalesOrRevenuesDocuments = [];
    if (payload.fixedCriteria?.includes('step34_fpci_pm_real.question_16.option_2')) {
        netSalesOrRevenuesDocuments = payload.netSalesOrRevenuesDocuments
            .reduce((acc, curr) => {
                if (curr?.id) {
                    acc = acc.concat(`api/documents/${curr.id}`);
                    return acc;
                }
                return acc;
            }, [])
            .concat(savedNetSalesOrRevenuesDocs.map(doc => `api/documents/${doc.id}`));
    }

    let higherEquityDocuments = [];
    if (payload.fixedCriteria?.includes('step34_fpci_pm_real.question_16.option_3')) {
        higherEquityDocuments = payload.higherEquityDocuments
            .reduce((acc, curr) => {
                if (curr?.id) {
                    acc = acc.concat(`api/documents/${curr.id}`);
                    return acc;
                }
                return acc;
            }, [])
            .concat(savedHigherEquityDocs.map(doc => `api/documents/${doc.id}`));
    }

    let exemptionConditionsProof = null;
    if (savedExemptionConditionsProofDocs.length) {
        exemptionConditionsProof = `api/documents/${savedExemptionConditionsProofDocs[0]?.id}`;
    } else if (payload.exemptionConditionsProof?.[0]?.id) {
        exemptionConditionsProof = `api/documents/${payload.exemptionConditionsProof?.[0]?.id}`;
    }

    return {
        subscriptions: [
            {
                investmentCharacteristics: {
                    investorQualification: payload.investorQualification,
                    investingSum: parseInt(payload.investingSum, 10),
                    fundsSource: payload.fundsSource,
                    fundsOrigin: JSON.stringify({
                        choice: payload.fundsOrigin,
                        other: payload.other,
                    }),
                    subscriptionRequestMotivation: payload.subscriptionRequestMotivation,
                    internalContactUser: payload.internalContactUser
                        ? `api/users/${payload.internalContactUser}`
                        : null,
                    nameOfEntity: payload.nameOfEntity,
                    nameOfFIA: payload.nameOfFIA,
                    durationOfHolding: payload.durationOfHolding,
                    fixedCriteria: JSON.stringify(payload.fixedCriteria),
                    balanceSheetTotalDocuments,
                    netSalesOrRevenuesDocuments,
                    higherEquityDocuments,
                    exemptionConditionsProof,
                    ...(subcategory
                        ? { shareSubcategory: `/api/share_subcategories/${subcategory}` }
                        : {}),
                },
                userAdequacy: {
                    transactionsCompleted: userAdequacy.transactionsCompleted,
                    professionalClient: userAdequacy.professionalClient,
                    portfolioValue: userAdequacy.portfolioValue,
                    professionalPositionHeld: userAdequacy.professionalPositionHeld,
                    transactionsCompletedDocuments: userAdequacy.transactionsCompletedDocuments,
                    portfolioValueDocuments: userAdequacy.portfolioValueDocuments,
                    professionalPositionHeldDocuments:
                        userAdequacy.professionalPositionHeldDocuments,
                },
            },
        ],
    };
};

export const sendUserKnowledge = payload => ({
    subscriptions: [
        {
            userKnowledge: {
                longTermFund: payload.longTermFund,
                capitalGuarantee: payload.capitalGuarantee,
                fundIntention: payload.fundIntention,
                fundPerformance: payload.fundPerformance,
                futurePerformanceIndex: payload.futurePerformanceIndex,
                fundAdmission: payload.fundAdmission,
                fundRisk: payload.fundRisk,
                assetDiversification: payload.assetDiversification,
                listedShares: payload.listedShares?.[0]
                    ? JSON.stringify(payload.listedShares)
                    : null,
                unlistedShares: payload.unlistedShares?.[0]
                    ? JSON.stringify(payload.unlistedShares)
                    : null,
                opcvm: payload.opcvm?.[0] ? JSON.stringify(payload.opcvm) : null,
                scpi: payload.scpi?.[0] ? JSON.stringify(payload.scpi) : null,
                privateEquityFund: payload.privateEquityFund?.[0]
                    ? JSON.stringify(payload.privateEquityFund)
                    : null,
                knowledgeOrigin: JSON.stringify({
                    choice: payload.knowledgeOrigin.choice,
                    other:
                        payload.knowledgeOrigin.choice === 'general_options.other_2'
                            ? payload.knowledgeOrigin.other
                            : null,
                }),
            },
        },
    ],
});

export const sendUserAdequacy = form => ({
    subscriptions: [
        {
            userAdequacy: {
                question1: form.question1,
                question2: convertToBoolOrNull(form.question2, 'Oui', 'Non'),
                question3: form.question3,
                question4: form.question4,
                question5: form.question5,
                question6: form.question6,
                question7: form.question7,
                question8: convertToBoolOrNull(form.question8, 'Oui', 'Non'),
                question9: form.question9,
                question10: form.question10,
                question11: form.question11,
                question12: form.question12,
                question13: form.question13,
                question14: form.question14,
                question15: JSON.stringify({
                    choice: [form.question15.choice],
                    ...(form.question15.choice === 'step37.question_15.option_4' && {
                        other: form.question15.other,
                    }),
                }),
            },
        },
    ],
});

export const sendSurveyResultsDocs = payload => {
    if (payload.professionalClient) {
        let transactionsCompletedFormData = null;
        if (payload.transactionsCompleted === 'true') {
            payload.transactionsCompletedDocuments.forEach(doc => {
                if (doc && !doc.id) {
                    if (!transactionsCompletedFormData) {
                        transactionsCompletedFormData = new FormData();
                    }
                    transactionsCompletedFormData.append('files[]', doc);
                }
            });
            transactionsCompletedFormData?.append('type', docTypes.OTHER);
            transactionsCompletedFormData?.append('subtype', 'TRANSACTIONS_COMPLETED');
        }

        let portfolioValueFormData = null;
        if (payload.portfolioValue === 'true') {
            payload.portfolioValueDocuments.forEach(doc => {
                if (doc && !doc.id) {
                    if (!portfolioValueFormData) {
                        portfolioValueFormData = new FormData();
                    }
                    portfolioValueFormData.append('files[]', doc);
                }
            });
            portfolioValueFormData?.append('type', docTypes.OTHER);
            portfolioValueFormData?.append('subtype', 'PORTFOLIO_VALUE');
        }

        let professionalPositionHeldFormData = null;
        if (payload.professionalPositionHeld === 'true') {
            payload.professionalPositionHeldDocuments.forEach(doc => {
                if (doc && !doc.id) {
                    if (!professionalPositionHeldFormData) {
                        professionalPositionHeldFormData = new FormData();
                    }
                    professionalPositionHeldFormData.append('files[]', doc);
                }
            });
            professionalPositionHeldFormData?.append('type', docTypes.OTHER);
            professionalPositionHeldFormData?.append('subtype', 'PROFESSIONAL_POSITION_HELD');
        }

        return [
            transactionsCompletedFormData,
            portfolioValueFormData,
            professionalPositionHeldFormData,
        ];
    } else {
        return [null, null, null];
    }
};

export const sendSurveyResults = (
    payload,
    transactionsCompletedDocs,
    portfolioValueDocs,
    professionalPositionHeldDocs
) => {
    if (payload.professionalClient) {
        const documentsToDelete = [];
        let transactionsCompletedDocuments = [];
        if (payload.transactionsCompleted === 'true') {
            transactionsCompletedDocuments = payload.transactionsCompletedDocuments
                .reduce((acc, curr) => {
                    if (curr?.id) {
                        acc = acc.concat(`api/documents/${curr.id}`);
                        return acc;
                    }
                    return acc;
                }, [])
                .concat(transactionsCompletedDocs.map(doc => `api/documents/${doc.id}`));
        } else {
            payload.transactionsCompletedDocuments.forEach(doc => {
                if (doc.id) {
                    documentsToDelete.push(doc.id);
                }
            });
        }

        let portfolioValueDocuments = [];
        if (payload.portfolioValue === 'true') {
            portfolioValueDocuments = payload.portfolioValueDocuments
                .reduce((acc, curr) => {
                    if (curr?.id) {
                        acc = acc.concat(`api/documents/${curr.id}`);
                        return acc;
                    }
                    return acc;
                }, [])
                .concat(portfolioValueDocs.map(doc => `api/documents/${doc.id}`));
        } else {
            payload.portfolioValueDocuments.forEach(doc => {
                if (doc.id) {
                    documentsToDelete.push(doc.id);
                }
            });
        }

        let professionalPositionHeldDocuments = [];
        if (payload.professionalPositionHeld === 'true') {
            professionalPositionHeldDocuments = payload.professionalPositionHeldDocuments
                .reduce((acc, curr) => {
                    if (curr?.id) {
                        acc = acc.concat(`api/documents/${curr.id}`);
                        return acc;
                    }
                    return acc;
                }, [])
                .concat(professionalPositionHeldDocs.map(doc => `api/documents/${doc.id}`));
        } else {
            payload.professionalPositionHeldDocuments.forEach(doc => {
                if (doc.id) {
                    documentsToDelete.push(doc.id);
                }
            });
        }

        return [
            documentsToDelete,
            {
                subscriptions: [
                    {
                        userAdequacy: {
                            professionalClient: true,
                            transactionsCompleted: payload.transactionsCompleted === 'true',
                            portfolioValue: payload.portfolioValue === 'true',
                            professionalPositionHeld: payload.professionalPositionHeld === 'true',
                            transactionsCompletedDocuments,
                            portfolioValueDocuments,
                            professionalPositionHeldDocuments,
                        },
                    },
                ],
            },
        ];
    } else {
        const documentsToDelete = [];
        payload.transactionsCompletedDocuments
            .concat(
                payload.portfolioValueDocuments.concat(payload.professionalPositionHeldDocuments)
            )
            .forEach(doc => {
                if (doc.id) {
                    documentsToDelete.push(doc.id);
                }
            });
        return [
            documentsToDelete,
            {
                subscriptions: [
                    {
                        userAdequacy: {
                            professionalClient: false,
                            transactionsCompleted: false,
                            portfolioValue: false,
                            professionalPositionHeld: false,
                            transactionsCompletedDocuments: [],
                            portfolioValueDocuments: [],
                            professionalPositionHeldDocuments: [],
                        },
                    },
                ],
            },
        ];
    }
};

export const legalRepresentativeDocPayload = ({ corporateInfo }) => {
    let payload = null;
    if (
        corporateInfo.legalRepresentativeDocument &&
        !corporateInfo.legalRepresentativeDocument?.id
    ) {
        // if there is no id the document was added at this submit so it must be uploaded
        payload = new FormData();
        payload.append('files[]', corporateInfo.legalRepresentativeDocument);
        payload?.append('type', docTypes.COMPANY);
    }

    return payload;
};

export const sendInvestorEligibilityDocs = payload => {
    let balanceSheetTotalFormData = null;
    if (payload.fixedCriteria?.includes('step34_fpci_pm_real.question_16.option_1')) {
        payload.balanceSheetTotalDocuments.forEach(doc => {
            if (doc && !doc.id) {
                if (!balanceSheetTotalFormData) {
                    balanceSheetTotalFormData = new FormData();
                }
                balanceSheetTotalFormData.append('files[]', doc);
            }
        });
        balanceSheetTotalFormData?.append('type', docTypes.OTHER);
        balanceSheetTotalFormData?.append('subtype', 'BALANCE_SHEET_TOTAL');
    }

    let netSalesOrRevenuesFormData = null;
    if (payload.fixedCriteria?.includes('step34_fpci_pm_real.question_16.option_2')) {
        payload.netSalesOrRevenuesDocuments.forEach(doc => {
            if (doc && !doc.id) {
                if (!netSalesOrRevenuesFormData) {
                    netSalesOrRevenuesFormData = new FormData();
                }
                netSalesOrRevenuesFormData.append('files[]', doc);
            }
        });
        netSalesOrRevenuesFormData?.append('type', docTypes.OTHER);
        netSalesOrRevenuesFormData?.append('subtype', 'NET_SALES_OR_REVENUES');
    }

    let higherEquityFormData = null;
    if (payload.fixedCriteria?.includes('step34_fpci_pm_real.question_16.option_3')) {
        payload.higherEquityDocuments.forEach(doc => {
            if (doc && !doc.id) {
                if (!higherEquityFormData) {
                    higherEquityFormData = new FormData();
                }
                higherEquityFormData.append('files[]', doc);
            }
        });
        higherEquityFormData?.append('type', docTypes.OTHER);
        higherEquityFormData?.append('subtype', 'HIGHER_EQUITY');
    }
    let exemptionConditionsProofFormData = null;
    if (payload.exemptionConditionsProof.length > 0) {
        payload.exemptionConditionsProof.forEach(doc => {
            if (doc && !doc.id) {
                if (!exemptionConditionsProofFormData) {
                    exemptionConditionsProofFormData = new FormData();
                }
                exemptionConditionsProofFormData.append('files[]', doc);
            }
        });
        exemptionConditionsProofFormData?.append('type', docTypes.OTHER);
        exemptionConditionsProofFormData?.append('subtype', 'EXEMPTION_CONDITION_PROOF');
    }

    return [
        balanceSheetTotalFormData,
        netSalesOrRevenuesFormData,
        higherEquityFormData,
        exemptionConditionsProofFormData,
    ];
};

export const initDistributorKYC = distributorCompany => ({
    assetManagementAmount: distributorCompany.assetManagementAmount || '',
    privateEquityAssetManagementPercentage:
        distributorCompany.privateEquityAssetManagementPercentage || '',
    professionalClientPercentage: distributorCompany.professionalClientPercentage || '',
    informedClientPercentage: distributorCompany.informedClientPercentage || '',
    nonProfessionalClientPercentage: distributorCompany.nonProfessionalClientPercentage || '',
    vatNumber: distributorCompany?.vatNumber || '',
    rcsNumber: distributorCompany?.rcsNumber || '',
    rcsRegistrationAddress: distributorCompany?.rcsRegistrationAddress || '',
    rcsRegistrationDate: distributorCompany?.rcsRegistrationDate
        ? moment(distributorCompany?.rcsRegistrationDate)
        : null,
    registrationDate: distributorCompany?.rcsRegistrationDate
        ? moment(distributorCompany?.rcsRegistrationDate).format('L')
        : null,
    oriasNumber: distributorCompany?.oriasNumber || '',
    acprNumber: distributorCompany?.acprNumber || '',
    distributionWays: distributorCompany.distributionWays || '',
    averagePrivateEquityTicketAmount: distributorCompany.averagePrivateEquityTicketAmount || '',
    categoryCommercialization:
        distributorCompany.publicCategoryCommercialization &&
        distributorCompany.privateCategoryCommercialization
            ? 'both'
            : distributorCompany.publicCategoryCommercialization
            ? 'public'
            : distributorCompany.privateCategoryCommercialization
            ? 'private'
            : '',
});

export const initKYCPM = kyc => ({
    totalBalanceLevel: kyc?.totalBalanceLevel || '',
    totalTurnoverLevel: kyc?.totalTurnoverLevel || '',
    totalEquityLevel: kyc?.totalEquityLevel || '',
    heritageAmount: kyc?.heritageAmount || '',
    realEstatePercentage: kyc?.realEstatePercentage || '',
    listedSharePercentage: kyc?.listedSharePercentage || '',
    unlistedSharePercentage: kyc?.unlistedSharePercentage || '',
    opcvmPercentage: kyc?.opcvmPercentage || '',
    availableCashPercentage: kyc?.availableCashPercentage || '',
    assetInvestor: kyc?.assetInvestor ? boolToStr(kyc?.assetInvestor, 'Oui', 'Non') : 'Non',
    assetPercentage: kyc?.assetPercentage || '',
    indebtednessAmount: kyc?.indebtednessAmount === null ? '' : kyc?.indebtednessAmount,
    mainResidencePercentage: kyc?.mainResidencePercentage || '',
    rentalInvestmentPercentage: kyc?.rentalInvestmentPercentage || '',
    otherCommitmentsPercentage: kyc?.otherCommitmentsPercentage || '',
    loanRepaymentPercentage: kyc?.loanRepaymentPercentage || '',
    otherDebtsPercentage: kyc?.otherDebtsPercentage || '',
});

export const sendDistributorKYC = payload => ({
    distributorCompany: {
        assetManagementAmount: payload.assetManagementAmount,
        rcsRegistrationDate: payload.rcsRegistrationDate || null,
        rcsRegistrationAddress: payload.rcsRegistrationAddress,
        rcsNumber: payload.rcsNumber,
        vatNumber: payload.vatNumber,
        oriasNumber: payload.oriasNumber,
        acprNumber: payload.acprNumber,
        privateEquityAssetManagementPercentage: +payload.privateEquityAssetManagementPercentage,
        professionalClientPercentage:
            typeof payload.professionalClientPercentage !== 'number'
                ? null
                : payload.professionalClientPercentage,
        informedClientPercentage:
            typeof payload.informedClientPercentage !== 'number'
                ? null
                : payload.informedClientPercentage,
        nonProfessionalClientPercentage:
            typeof payload.nonProfessionalClientPercentage !== 'number'
                ? null
                : payload.nonProfessionalClientPercentage,
        averagePrivateEquityTicketAmount: +payload.averagePrivateEquityTicketAmount,
        distributionWays: payload.distributionWays,
        publicCategoryCommercialization:
            payload.categoryCommercialization === 'public' ||
            payload.categoryCommercialization === 'both',
        privateCategoryCommercialization:
            payload.categoryCommercialization === 'private' ||
            payload.categoryCommercialization === 'both',
    },
});

export const sendCustomStepData = payload => ({
    subscriptions: [
        {
            userTunnelSteps: [
                {
                    answers: Object.values(payload) // a list of all answers of the step
                        .reduce((acc, crt) => [...acc, ...crt], [])
                        .filter(a => a.value)
                        .map(answer => ({ answerId: answer.id, title: answer.value + '' })),
                },
            ],
        },
    ],
});
